import { Bars, Card, ProvenanceTag } from "@/ui";
import { browsers, devices, geo } from "@/mock";
import type { GeoRow } from "@/mock";
import { fmt } from "@/lib/format";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useDevices, useGeo } from "@/data/hooks";
import { PanelHeader } from "../parts";
import { useSignalsQuery, matches } from "../searchContext";
import { AigenTag, EmptyState, SampleChip, Skeleton, panelState } from "../wiring";

/** Calm geo intensity — a tiny inline-SVG dotted world with weighted blips,
 *  no map library. Positions are hand-placed on a 0..1 grid. */
const GEO_XY: Record<string, { x: number; y: number }> = {
  US: { x: 0.22, y: 0.42 },
  CA: { x: 0.24, y: 0.3 },
  GB: { x: 0.47, y: 0.34 },
  FR: { x: 0.49, y: 0.4 },
  DE: { x: 0.52, y: 0.36 },
  AZ: { x: 0.62, y: 0.42 },
  IN: { x: 0.7, y: 0.55 },
};

export function Audience() {
  const { siteId, range, live } = useWorkspace();
  const devicesQ = useDevices(siteId, range);
  const geoQ = useGeo(siteId, range);

  const state = panelState({
    siteId,
    workspaceLive: live,
    isLoading: devicesQ.isLoading || geoQ.isLoading,
    isError: devicesQ.isError || geoQ.isError,
    hasData: !!devicesQ.data || !!geoQ.data,
  });

  const isLive = state === "live";

  const deviceRows =
    isLive && devicesQ.data?.items?.length
      ? devicesQ.data.items.map((it) => ({ label: it.device ?? "Unknown", value: it.visits }))
      : devices.map((d) => ({ label: d.label, value: d.value, sub: d.sub }));

  const geoRows: GeoRow[] =
    isLive && geoQ.data?.items?.length
      ? geoQ.data.items.map((it) => ({
          code: (it.country ?? "??").toUpperCase(),
          label: it.country_name ?? it.country ?? "Unknown",
          value: it.visits,
          pct: 0,
        }))
      : geo;

  const q = useSignalsQuery();
  const deviceShown = deviceRows.filter((d) => matches(q, d.label));
  const geoShown = geoRows.filter((g) => matches(q, g.label, g.code));
  const max = Math.max(...geoShown.map((g) => g.value), 1);
  const geoEmpty = (isLive && (!geoQ.data || geoQ.data.items.length === 0)) || (!!q && geoShown.length === 0);
  const deviceEmpty = (isLive && (!devicesQ.data || devicesQ.data.items.length === 0)) || (!!q && deviceShown.length === 0);

  if (state === "loading") {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-[240px]" />
          <Skeleton className="h-[240px]" />
        </div>
        <Skeleton className="h-[320px]" />
      </div>
    );
  }

  return (
    <div className="stagger space-y-6">
      <PanelHeader
        title="Audience"
        desc="Who your visitors are — device and where in the world."
        action={
          isLive ? <AigenTag /> : (
            <span className="flex items-center gap-2">
              <ProvenanceTag source="matomo" />
              <SampleChip />
            </span>
          )
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">Devices</h3>
            {isLive ? (
              <AigenTag />
            ) : (
              <span className="flex items-center gap-2">
                <ProvenanceTag source="matomo" />
                <SampleChip />
              </span>
            )}
          </div>
          {deviceEmpty ? (
            <EmptyState title={q ? `No devices match “${q}”` : "No device data yet"} />
          ) : (
            <Bars data={deviceShown} accentTop />
          )}
        </Card>
        {/* Browsers: no first-party browser breakdown endpoint yet → sample. */}
        <Card className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">Browsers</h3>
            <span className="flex items-center gap-2">
              <ProvenanceTag source="matomo" />
              <SampleChip />
            </span>
          </div>
          <Bars data={browsers.map((b) => ({ label: b.label, value: b.value }))} />
        </Card>
      </div>

      <Card className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">By region</h3>
          {isLive ? (
            <AigenTag />
          ) : (
            <span className="flex items-center gap-2">
              <ProvenanceTag source="matomo" />
              <SampleChip />
            </span>
          )}
        </div>
        {geoEmpty ? (
          <EmptyState
            title={q ? `No regions match “${q}”` : "No regions yet"}
            detail={q ? "Try a different search term." : "Visitor regions appear once traffic arrives."}
          />
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* calm inline-SVG region representation (only codes we can place) */}
            <div className="rounded-card bg-paper-2 p-4 dark:bg-white/5">
              <svg viewBox="0 0 100 60" className="w-full" role="img" aria-label="Visitor regions">
                {[15, 30, 45].map((y) => (
                  <line key={y} x1="4" x2="96" y1={y} y2={y} stroke="var(--grid-line)" strokeWidth="0.4" />
                ))}
                {[25, 50, 75].map((x) => (
                  <line key={x} x1={x} x2={x} y1="6" y2="54" stroke="var(--grid-line)" strokeWidth="0.4" />
                ))}
                {geoShown.map((g) => {
                  const pos = GEO_XY[g.code];
                  if (!pos) return null;
                  const w = g.value / max;
                  const r = 1.6 + w * 5;
                  const top = g.value === max;
                  return (
                    <g key={g.code}>
                      <circle
                        cx={pos.x * 100}
                        cy={pos.y * 60}
                        r={r}
                        fill={top ? "var(--accent)" : "var(--ink-600)"}
                        opacity={top ? 0.85 : 0.18 + w * 0.4}
                      />
                      <text
                        x={pos.x * 100}
                        y={pos.y * 60 - r - 1.2}
                        textAnchor="middle"
                        fontSize="2.6"
                        fill="var(--ink-600)"
                        opacity="0.7"
                        fontFamily="monospace"
                      >
                        {g.code}
                      </text>
                    </g>
                  );
                })}
              </svg>
              <p className="mt-2 font-mono text-[10px] text-subtle">
                circle size = visitor share · coral = top region
              </p>
            </div>

            {/* region list */}
            <div className="flex flex-col gap-2.5">
              {geoShown.map((g, i) => (
                <div key={g.code} className="group">
                  <div className="mb-1 flex items-baseline justify-between gap-2 text-[13px]">
                    <span className="flex items-center gap-2 truncate text-ink">
                      <span className="font-mono text-[11px] text-subtle">{g.code}</span>
                      {g.label}
                    </span>
                    <span className="shrink-0 font-mono text-[12px] text-muted">{fmt(g.value)}</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-paper-2 dark:bg-white/5">
                    <div
                      className={
                        "h-full rounded-full transition-[width] duration-500 ease-out-soft " +
                        (i === 0 ? "bg-accent" : "bg-ink-strong/70 dark:bg-white/40")
                      }
                      style={{ width: `${(g.value / max) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
